import debounce from '@material-ui/core/utils/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash/get';

const CORE_FIELDS = [
  'configurationCode',
  'emailAddress',
  'name',
  'company',
  'phoneNumber',
  'street',
  'zip',
  'city',
  'country',
];
const alwaysValid = () => true;

const getCurrentFields = (itemIdentifier, formFields) =>
  Object.keys(formFields)
    .filter(
      (name) => !formFields[name].allowedItems || formFields[name].allowedItems.includes(itemIdentifier)
    )
    .reduce((result, name) => ({ ...result, [name]: formFields[name] }), {});

export const useForm = (fields, options = {}) => {
  const { validatorFn = alwaysValid } = options;

  const itemIdentifier = useSelector((state) => state.Configuration.configuration?.item?.identifier);
  const currentFields = useMemo(() => getCurrentFields(itemIdentifier, fields), [itemIdentifier, fields]);

  const [formData, setFormData] = useState(currentFields);

  useEffect(() => {
    setFormData(currentFields);
  }, [currentFields]);

  const changeHandler = useCallback(
    (name, value) => {
      setFormData((formData) => {
        const fieldData = { ...formData[name], value };
        return {
          ...formData,
          [name]: validatorFn(fieldData),
        };
      });
    },
    [setFormData, validatorFn]
  );

  const debouncedChangeHandler = useMemo(() => debounce(changeHandler, 300), [changeHandler]);

  const validate = useCallback(() => {
    const validatedFields = {};
    let isAllValid = true;
    for (const key of Object.keys(currentFields)) {
      const validatedFieldData = validatorFn(formData[key]);
      validatedFields[key] = validatedFieldData;
      isAllValid = isAllValid && !validatedFieldData.error;
    }
    !isAllValid && setFormData(validatedFields);
    return isAllValid;
  }, [formData, currentFields, validatorFn, setFormData]);

  const reset = useCallback(() => setFormData(currentFields), [currentFields, setFormData]);

  const getValues = () => {
    const values = {};
    for (const key of Object.keys(currentFields)) {
      if (formData[key].type !== 'label') {
        values[key] = formData[key].value;
      }
    }
    return values;
  };

  const getOfferRequestValues = () => {
    const values = {};
    values.additional = {};
    for (const key of Object.keys(currentFields)) {
      if (formData[key].type !== 'label') {
        if (CORE_FIELDS.indexOf(key) === -1) {
          values.additional[key] = formData[key].value;
        } else {
          values[key] = formData[key].value;
        }
      }
    }
    values['name'] = `${get(values, 'additional.firstname', '-')} ${get(values, 'additional.surname', '-')}`;
    return values;
  };

  return {
    formData,
    changeHandler,
    debouncedChangeHandler,
    validate,
    reset,
    getValues,
    getOfferRequestValues,
  };
};
